import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Timer, Wind, CheckCircle2, PenLine } from "lucide-react";
import { api, localDate, moodLabel } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { StreakBadges } from "@/components/StreakBadges";

const dayLetter = (iso) => ["S", "M", "T", "W", "T", "F", "S"][new Date(`${iso}T12:00:00`).getDay()];
const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

export const PracticeLog = ({ onData }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/practice", { params: { today: localDate() } }).then((r) => { setData(r.data); onData?.(r.data); }).catch(() => setData({ stats: {}, days: [], sessions: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) return <Skeleton className="h-56 rounded-3xl" />;
  const { stats, days, sessions, badges } = data;
  const today = localDate();

  return (
    <section className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 space-y-6" data-testid="practice-log">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Practice log</p>
          <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">Your breathing practice</h2>
        </div>
        <div className="flex gap-6">
          <div className="text-right">
            <p className="font-display text-3xl font-extrabold text-amber-600 flex items-center gap-1 justify-end" data-testid="practice-streak"><Flame className="w-6 h-6" />{stats.current_streak || 0}</p>
            <p className="text-xs text-slate-500">day streak{stats.longest_streak > (stats.current_streak || 0) ? ` · best ${stats.longest_streak}` : ""}</p>
          </div>
          <div className="text-right">
            <p className="font-display text-3xl font-extrabold text-slate-900" data-testid="practice-total-minutes">{stats.total_minutes || 0}</p>
            <p className="text-xs text-slate-500">minutes total</p>
          </div>
          <div className="text-right">
            <p className="font-display text-3xl font-extrabold text-slate-900" data-testid="practice-total-sessions">{stats.total_sessions || 0}</p>
            <p className="text-xs text-slate-500">sessions</p>
          </div>
        </div>
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Last 14 days</p>
        <div className="grid grid-cols-14 gap-1.5" style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }} data-testid="practice-days">
          {days.map((d) => {
            const mins = Math.round(d.seconds / 60);
            const tone = d.seconds === 0 ? "bg-slate-100" : mins < 3 ? "bg-emerald-200" : mins < 8 ? "bg-emerald-400" : "bg-emerald-600";
            return (
              <div key={d.date} className="flex flex-col items-center gap-1" title={`${fmtDate(d.date)}: ${mins} min`}>
                <div className={`w-full aspect-square rounded-md ${tone} ${d.date === today ? "ring-2 ring-indigo-400 ring-offset-1" : ""}`} data-testid={`practice-day-${d.date}`} />
                <span className="text-[10px] text-slate-400">{dayLetter(d.date)}</span>
              </div>
            );
          })}
        </div>
        <p className="text-sm text-slate-500 mt-3" data-testid="practice-nudge">
          {stats.practised_today ? "You've practised today — lovely. Rest is part of the rhythm too."
            : (stats.current_streak || 0) > 0 ? `Keep your ${stats.current_streak}-day streak alive with one short session today.`
              : "A single minute of breathing today starts a streak. No pressure — whenever you're ready."}
        </p>
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Streak milestones</p>
        <StreakBadges badges={badges} compact />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Recent sessions</p>
          <Link to="/reflections" className="text-xs font-semibold text-indigo-600 hover:underline inline-flex items-center gap-1" data-testid="practice-reflections-link"><PenLine className="w-3.5 h-3.5" /> Reflection journal</Link>
        </div>
        {sessions.length === 0 ? (
          <p className="text-sm text-slate-500 rounded-2xl border border-dashed border-slate-200 p-6 text-center" data-testid="practice-empty">
            No sessions yet. Try <Link to="/" className="text-indigo-600 font-semibold hover:underline">Calm Breath</Link> — three minutes, no breath-hold.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="practice-sessions">
            {sessions.slice(0, 8).map((s) => (
              <li key={s.id} className="flex items-center gap-3 py-3 text-sm" data-testid={`practice-session-${s.id}`}>
                <span className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${s.completed ? "bg-emerald-50 text-emerald-600" : "bg-indigo-50 text-indigo-600"}`}>
                  {s.completed ? <CheckCircle2 className="w-4 h-4" /> : <Wind className="w-4 h-4" />}
                </span>
                <div className="flex-1 min-w-0">
                  <Link to={`/tutorials/${s.tutorial_id}`} className="font-medium text-slate-900 hover:text-indigo-700 truncate block">{s.tutorial_title}</Link>
                  <p className="text-xs text-slate-500">{fmtDate(s.local_date)} · {s.rounds} round{s.rounds === 1 ? "" : "s"}{s.completed ? "" : " · stopped early"}{s.mood_before && s.mood_after ? ` · ${moodLabel(s.mood_before)} → ${moodLabel(s.mood_after)}` : ""}</p>
                  {s.reflection && <p className="text-xs text-indigo-700 italic mt-0.5 truncate" data-testid={`practice-reflection-${s.id}`}>“{s.reflection}”</p>}
                </div>
                <span className="flex items-center gap-1 text-slate-700 font-semibold shrink-0"><Timer className="w-3.5 h-3.5 text-slate-400" />{s.minutes} min</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};
